/**
 * Checkout-page templates (Wave 6).
 *
 * Canned starting points a merchant can apply to a fresh checkout page.
 * Each template defines:
 *   - layout_blocks: full LayoutBlocks JSON (sections + blocks + config)
 *   - theme_overrides: matching visual tone
 *   - suggested payment_methods (optional — the merchant can override)
 *
 * Applied by the "Templates" button in the PageBuilder header.
 */

import type { CheckoutPageNode, LayoutBlocks } from "../../types/flow";

export interface CheckoutTemplate {
  id: string;
  name: string;
  description: string;
  layout_blocks: LayoutBlocks;
  theme_overrides?: CheckoutPageNode["theme_overrides"];
  suggested_payment_methods?: string[];
}

const FASHION: CheckoutTemplate = {
  id: "fashion",
  name: "Fashion / apparel",
  description:
    "Product thumbnails up top, size-aware address form, promo code + badges. Works for most apparel brands.",
  layout_blocks: {
    schema_version: 1,
    sections: {
      contact: { visible: true, blocks: ["email", "newsletter_opt_in"] },
      shipping: { visible: true, blocks: ["address", "shipping_method"] },
      offers: { visible: true, blocks: ["incentive_banner"] },
      payment: {
        visible: true,
        blocks: ["payment_method_selector", "save_card_toggle", "pay_button"],
      },
      legal: { visible: false, blocks: [] },
      support: { visible: false, blocks: [] },
      summary: {
        visible: true,
        blocks: ["product_thumbnails", "totals", "promo_code", "trust_badges"],
      },
    },
    block_config: {
      newsletter_opt_in: {
        default_opt_in: false,
        copy: "Sign me up for drops, restocks, and 10% off my next order.",
      },
      incentive_banner: {
        copy: "",
        target_amount_cents: 10000,
      },
      save_card_toggle: {
        default_checked: true,
        copy: "Save this card for faster checkout next time",
      },
      product_thumbnails: { size_px: 64 },
      trust_badges: { badge_ids: ["ssl", "money_back_30d"] },
    },
  },
  theme_overrides: {
    pay_button_color: "#111111",
    pay_button_text_color: "#ffffff",
    brand_color: "#111111",
    radius_px: 4,
  },
  suggested_payment_methods: ["card", "apple_pay", "google_pay", "klarna"],
};

const DIGITAL: CheckoutTemplate = {
  id: "digital",
  name: "Digital / SaaS",
  description:
    "No shipping section, terms + age verification in payment, support block at the bottom. Built for software sales.",
  layout_blocks: {
    schema_version: 1,
    sections: {
      contact: { visible: true, blocks: ["email", "login_link", "company_field"] },
      shipping: { visible: true, blocks: [] }, // required, but empty: buyer sees nothing
      offers: { visible: false, blocks: [] },
      payment: {
        visible: true,
        blocks: [
          "payment_method_selector",
          "save_card_toggle",
          "terms_checkbox",
          "pay_button",
        ],
      },
      legal: { visible: true, blocks: ["legal_links"] },
      support: { visible: true, blocks: ["support_block"] },
      summary: { visible: true, blocks: ["line_items", "totals"] },
    },
    block_config: {
      terms_checkbox: {
        required: true,
        copy: "I agree to the service agreement and acceptable-use policy",
        terms_url: "/terms",
      },
      legal_links: {
        terms_url: "/terms",
        privacy_url: "/privacy",
        refund_url: "/refund",
      },
      support_block: { email: "support@example.com", chat_provider: "none" },
      save_card_toggle: {
        default_checked: true,
        copy: "Save this card for recurring charges",
      },
      login_link: {
        copy: "Already a customer? Sign in",
        href: "/signin",
      },
      company_field: { required: false },
    },
  },
  theme_overrides: {
    brand_color: "#2563eb",
    pay_button_color: "#2563eb",
    pay_button_text_color: "#ffffff",
    radius_px: 6,
  },
  suggested_payment_methods: ["card"],
};

const SUBSCRIPTION: CheckoutTemplate = {
  id: "subscription",
  name: "Subscription",
  description:
    "Explicit MIT consent banner, save-card required, recurring-charge disclosure. Required for repeating charges.",
  layout_blocks: {
    schema_version: 1,
    sections: {
      contact: { visible: true, blocks: ["email"] },
      shipping: { visible: true, blocks: ["address", "shipping_method"] },
      offers: { visible: false, blocks: [] },
      payment: {
        visible: true,
        blocks: [
          "payment_method_selector",
          "save_card_toggle",
          "subscription_consent",
          "terms_checkbox",
          "pay_button",
        ],
      },
      legal: { visible: true, blocks: ["legal_links"] },
      support: { visible: true, blocks: ["help_center_link"] },
      summary: { visible: true, blocks: ["line_items", "totals", "promo_code"] },
    },
    block_config: {
      save_card_toggle: {
        default_checked: true,
        copy: "Save this card for future subscription charges",
      },
      subscription_consent: {
        copy:
          "Your cart includes a subscription. By paying, you authorize recurring charges at the stated interval until you cancel. You can cancel any time from your account.",
      },
      terms_checkbox: {
        required: true,
        copy: "I accept the subscription terms",
        terms_url: "/subscription-terms",
      },
      legal_links: {
        terms_url: "/subscription-terms",
        privacy_url: "/privacy",
        refund_url: "/refund",
      },
      help_center_link: {
        label: "How to cancel",
        url: "/help/cancel",
      },
    },
  },
  theme_overrides: {
    brand_color: "#059669",
    pay_button_color: "#059669",
    pay_button_text_color: "#ffffff",
    radius_px: 8,
  },
  suggested_payment_methods: ["card", "sepa"],
};

const B2B: CheckoutTemplate = {
  id: "b2b",
  name: "B2B",
  description:
    "Company + PO number + VAT ID, invoice-style summary, terms & conditions required. Built for bulk buyers.",
  layout_blocks: {
    schema_version: 1,
    sections: {
      contact: { visible: true, blocks: ["email", "company_field", "phone"] },
      shipping: {
        visible: true,
        blocks: ["address", "vat_id_field", "po_number", "shipping_method", "order_notes"],
      },
      offers: { visible: false, blocks: [] },
      payment: {
        visible: true,
        blocks: ["payment_method_selector", "billing_same_as_shipping", "terms_checkbox", "pay_button"],
      },
      legal: { visible: true, blocks: ["legal_links"] },
      support: { visible: true, blocks: ["support_block"] },
      summary: {
        visible: true,
        blocks: ["line_items", "totals", "tax_breakdown"],
      },
    },
    block_config: {
      company_field: { required: true },
      phone: { required: true },
      vat_id_field: { required: false, validate: false },
      po_number: { required: false },
      tax_breakdown: { show_if_no_tax: true },
      terms_checkbox: {
        required: true,
        copy: "I accept the enterprise purchase terms and NET30 payment terms",
        terms_url: "/enterprise-terms",
      },
      legal_links: {
        terms_url: "/enterprise-terms",
        privacy_url: "/privacy",
      },
      support_block: {
        email: "enterprise@example.com",
        chat_provider: "none",
      },
      billing_same_as_shipping: { default_checked: false },
    },
  },
  theme_overrides: {
    brand_color: "#0f172a",
    pay_button_color: "#0f172a",
    pay_button_text_color: "#ffffff",
    radius_px: 2,
    font_family: "'Inter', system-ui, sans-serif",
  },
  suggested_payment_methods: ["card", "sepa", "bank_transfer"],
};

const NORDIC: CheckoutTemplate = {
  id: "nordic",
  name: "Nordic",
  description:
    "Vipps/MobilePay top of mind, Swish support, tidy Scandinavian aesthetic, pickup-point shipping.",
  layout_blocks: {
    schema_version: 1,
    sections: {
      contact: { visible: true, blocks: ["email", "newsletter_opt_in"] },
      shipping: {
        visible: true,
        blocks: ["address", "pickup_point", "shipping_method"],
      },
      offers: { visible: false, blocks: [] },
      payment: {
        visible: true,
        blocks: ["payment_method_selector", "save_card_toggle", "pay_button"],
      },
      legal: { visible: true, blocks: ["legal_links"] },
      support: { visible: false, blocks: [] },
      summary: {
        visible: true,
        blocks: ["product_thumbnails", "totals", "promo_code"],
      },
    },
    block_config: {
      newsletter_opt_in: {
        default_opt_in: false,
        copy: "Sign up for our newsletter. GDPR — you can unsubscribe any time.",
      },
      product_thumbnails: { size_px: 48 },
      save_card_toggle: {
        default_checked: false,
        copy: "Save this card — I agree to the terms",
      },
      legal_links: {
        terms_url: "/terms",
        privacy_url: "/privacy",
        refund_url: "/refund",
        cookies_url: "/cookies",
      },
    },
  },
  theme_overrides: {
    brand_color: "#2c5af0",
    pay_button_color: "#2c5af0",
    pay_button_text_color: "#ffffff",
    bg_color: "#f7f8fa",
    radius_px: 12,
    font_family: "'Inter', system-ui, sans-serif",
  },
  suggested_payment_methods: ["card", "vipps_mobilepay", "swish", "klarna"],
};

export const CHECKOUT_TEMPLATES: CheckoutTemplate[] = [
  FASHION,
  DIGITAL,
  SUBSCRIPTION,
  B2B,
  NORDIC,
];
